import { isNotDefined } from '@sgme/fp';
import classNames from 'classnames';
import { AmountWithUnit, type AmountWithUnitProps } from 'components/shared/Amount/Amount';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { TradeSizeInputMode } from 'state/secondaryMarket/shared/tradeInformation.models';

export type AmountModeProps = Omit<AmountWithUnitProps, 'children'> & {
  isModeActive?: boolean;
  inputMode: TradeSizeInputMode;
  onTradeSizeInputModeChanged: () => void;
};
export function AmountMode({
  isModeActive = false,
  inputMode,
  onTradeSizeInputModeChanged,
  ...props
}: AmountModeProps) {
  const handleClick = useCallback(() => {
    if (!isModeActive) {
      onTradeSizeInputModeChanged();
    }
  }, [onTradeSizeInputModeChanged, isModeActive]);
  const { formatMessage } = useIntl();
  const title = useMemo(() => {
    const intlKey =
      inputMode === 'amount'
        ? 'secondaryMarket.tradeInformation.amount.radio-button-title'
        : 'secondaryMarket.tradeInformation.quantity.radio-button-title';

    return formatMessage({ id: intlKey });
  }, [inputMode, formatMessage]);

  const hasNoValue = useMemo(() => {
    return isNotDefined(props.value);
  }, [props.value]);

  return (
    <AmountWithUnit
      {...{ ...props, isReadOnly: !isModeActive, isDisabled: !isModeActive, isFocused: isModeActive && hasNoValue }}
      className={classNames(isModeActive ? undefined : 'text-muted', props.className)}
    >
      <div
        className="input-group-text opacity-100 px-0"
        style={{ boxShadow: `inset 0 -0.0625rem 0 rgba(var(--bs-secondary-rgb), ${isModeActive ? 0.0 : 0.2})` }}
      >
        <button
          data-e2e="lock-unlock-button"
          className="btn btn-icon btn-discreet-secondary px-0"
          type="button"
          role="tooltip"
          onClick={handleClick}
          disabled={props.isDisabled || props.isLoading}
          tabIndex={props.tabIndex}
          title={title}
        >
          <i className={classNames('icon icon-sm')}>{!isModeActive ? 'lock' : 'lock_open'}</i>
        </button>
      </div>
    </AmountWithUnit>
  );
}
